import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hasManualSpacing, longestEmptyParagraphRun, looksLikeManualList, readDocument } from "@/office/word/document";

export const lektionSuchenAbschluss: Lesson = {
  id: "abschlussuebung",
  title: "Abschlussübung: Ein fremdes Dokument aufräumen",
  summary: "Suchen und Ersetzen als schnellstes Werkzeug gegen Behelfslösungen.",
  durationMinutes: 10,
  learningGoals: [
    "Ein übernommenes Dokument systematisch bereinigen",
    "Sonderformate in der Suche einsetzen",
  ],
  steps: [
    {
      id: "aufraeumen",
      title: "Das Dokument bereinigen",
      explanation: [
        "In der Praxis bekommst du Dokumente von anderen – und die enthalten fast immer " +
          "die Behelfslösungen aus den ersten Modulen: Leerzeilen als Abstand, " +
          "Tabulatoren als Einzug, getippte Striche als Aufzählung.",
        "Mit Suchen und Ersetzen räumst du das in wenigen Minuten auf, statt jede Stelle " +
          "einzeln anzufassen. Die wichtigsten Sonderformate dafür: ^p für die " +
          "Absatzmarke, ^t für den Tabulator, ^l für den manuellen Zeilenumbruch.",
        "Baue dir für diese Übung selbst ein „unsauberes“ Dokument – mit Leerzeilen, " +
          "Tabulatoren und getippten Aufzählungsstrichen – und räume es anschließend auf.",
      ],
      keyPoints: [
        "^p Absatzmarke, ^t Tabulator, ^l manueller Zeilenumbruch.",
        "^p^p durch ^p ersetzen entfernt Leerzeilen.",
        "Mehrfach ausführen, bis Word 0 Ersetzungen meldet.",
      ],
      task: {
        instruction:
          "Dein Dokument soll mindestens fünf Absätze Text enthalten und danach frei sein " +
          "von: Leerzeilen, Tabulatoren beziehungsweise Leerzeichenketten und getippten " +
          "Aufzählungsstrichen.",
        hint: "Am schnellsten mit Strg + H und den Sonderformaten – nicht von Hand löschen.",
        check: async () => {
          const snapshot = await readDocument();
          if (snapshot.filledParagraphs.length < 5) {
            return fail(
              `Es gibt erst ${snapshot.filledParagraphs.length} Absätze mit Inhalt.`,
              "Schreibe zuerst einen Text mit mindestens fünf Absätzen.",
            );
          }

          const leerzeilen = snapshot.paragraphs.filter((absatz) => absatz.text.length === 0).length;
          const tabulatoren = snapshot.filledParagraphs.filter(hasManualSpacing).length;
          const striche = snapshot.filledParagraphs.filter(looksLikeManualList).length;

          return fromCriteria(
            [
              criterion(`Keine Leerzeilen (gefunden: ${leerzeilen})`, leerzeilen === 0),
              criterion(
                `Längste Leerzeilenkette: ${longestEmptyParagraphRun(snapshot)}`,
                longestEmptyParagraphRun(snapshot) === 0,
              ),
              criterion(
                `Keine Tabulatoren oder Leerzeichenketten (gefunden: ${tabulatoren})`,
                tabulatoren === 0,
              ),
              criterion(`Keine getippten Aufzählungsstriche (gefunden: ${striche})`, striche === 0),
            ],
            {
              success: "Aufgeräumt – und das ging deutlich schneller als von Hand.",
              failure: "Es ist noch etwas übrig:",
              hint: "Blende die Steuerzeichen mit Strg + Umschalt + 8 ein, dann siehst du genau, was noch im Dokument steckt.",
            },
          );
        },
      },
    },
    {
      id: "rueckblick",
      title: "Rückblick",
      explanation: [
        "Suchen und Ersetzen wird meist unterschätzt: Die meisten kennen nur das " +
          "Austauschen von Wörtern. Die Suche nach Formatierungen und Sonderformaten " +
          "macht daraus ein Werkzeug, mit dem sich ganze Dokumente in Minuten in Form " +
          "bringen lassen.",
      ],
      keyPoints: [
        "Strg + H, dann „Erweitern“ für alle Optionen.",
        "Über „Format“ nach Formatierung suchen, über „Sonderformat“ nach Steuerzeichen.",
        "Die Sprache klebt am Text – bei importierten Dokumenten zuerst prüfen.",
        "Silbentrennung folgt der Sprache und rettet den Blocksatz.",
      ],
    },
  ],
};
